// frontend/src/pages/admin/AdminInstallationRequestsPage.jsx
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Eye, Loader2, Search, CheckCircle, XCircle, Clock, 
  MapPin, Calendar, DollarSign, User, Phone, Home,
  RefreshCw, Filter
} from 'lucide-react';
import { PageSEO } from '../../components/SEO/PageSEO';
import api from '../../services/api';
import config from '../../config';

export const AdminInstallationRequestsPage = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  const pageSize = 20;

  useEffect(() => {
    fetchRequests();
  }, [currentPage, statusFilter]);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const accessToken = localStorage.getItem('access_token');
      const params = new URLSearchParams();
      params.append('page', currentPage);
      params.append('page_size', pageSize);
      if (statusFilter) params.append('status', statusFilter);
      
      const url = config.getApiUrl(`/installations/requests/?${params.toString()}`);
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${accessToken}` }
      });
      
      if (!response.ok) throw new Error('خطا در دریافت درخواست‌ها');
      
      const data = await response.json();
      setRequests(data.results || []);
      setTotalCount(data.count || 0);
      setTotalPages(Math.ceil((data.count || 0) / pageSize));
    } catch (error) {
      console.error('Error fetching requests:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'pending': { text: 'در انتظار پیشنهاد', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      'bidding': { text: 'در حال دریافت پیشنهاد', color: 'bg-blue-100 text-blue-800', icon: Clock },
      'quoted': { text: 'قیمت‌گذاری شده', color: 'bg-purple-100 text-purple-800', icon: CheckCircle },
      'accepted': { text: 'تایید شده', color: 'bg-green-100 text-green-800', icon: CheckCircle },
      'scheduled': { text: 'زمان‌بندی شده', color: 'bg-indigo-100 text-indigo-800', icon: Calendar },
      'in_progress': { text: 'در حال انجام', color: 'bg-orange-100 text-orange-800', icon: Clock },
      'completed': { text: 'تکمیل شده', color: 'bg-green-100 text-green-800', icon: CheckCircle },
      'cancelled': { text: 'لغو شده', color: 'bg-red-100 text-red-800', icon: XCircle },
    };
    const info = statusMap[status] || statusMap.pending;
    const Icon = info.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full ${info.color}`}>
        <Icon className="w-3 h-3" />
        {info.text}
      </span>
    );
  };

  const formatPrice = (price) => {
    if (!price) return '۰';
    return parseInt(price).toLocaleString('fa-IR');
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const filteredRequests = requests.filter(req => 
    req.customer?.phone?.includes(searchTerm) ||
    req.customer?.username?.includes(searchTerm) ||
    req.address?.includes(searchTerm)
  );

  if (loading && requests.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <>
      <PageSEO title="مدیریت درخواست‌های نصب | پنل ادمین" noindex={true} />
      
      <div>
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">درخواست‌های نصب</h1>
            <p className="text-sm text-gray-500 mt-1">مدیریت درخواست‌های نصب سیستم‌های خورشیدی</p>
          </div>
          <button
            onClick={() => { setRefreshing(true); fetchRequests(); }}
            disabled={refreshing}
            className="flex items-center gap-2 px-4 py-2 border rounded-lg hover:bg-gray-50 transition"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            بروزرسانی
          </button>
        </div>

        {/* فیلترها */}
        <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 relative">
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="جستجو در درخواست‌ها..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pr-10 pl-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">همه وضعیت‌ها</option>
              <option value="pending">در انتظار پیشنهاد</option>
              <option value="bidding">در حال دریافت پیشنهاد</option>
              <option value="quoted">قیمت‌گذاری شده</option>
              <option value="accepted">تایید شده</option>
              <option value="scheduled">زمان‌بندی شده</option>
              <option value="in_progress">در حال انجام</option>
              <option value="completed">تکمیل شده</option>
              <option value="cancelled">لغو شده</option>
            </select>
          </div>
        </div>

        {/* جدول درخواست‌ها */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">شناسه</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">مشتری</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">آدرس</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">مساحت</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">قیمت تخمینی</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">وضعیت</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">تاریخ</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">عملیات</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredRequests.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-12 text-center text-gray-500">
                      درخواستی یافت نشد
                    </td>
                  </tr>
                ) : (
                  filteredRequests.map((req) => (
                    <tr key={req.id} className="hover:bg-gray-50 transition">
                      <td className="px-6 py-4 text-gray-500">#{req.id}</td>
                      <td className="px-6 py-4">
                        <div>
                          <p className="font-medium">{req.customer?.username || '-'}</p>
                          <p className="text-xs text-gray-500">{req.customer?.phone}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4 max-w-xs truncate">{req.address}</td>
                      <td className="px-6 py-4">{req.roof_area} m²</td>
                      <td className="px-6 py-4">
                        {req.estimated_cost ? `${formatPrice(req.estimated_cost)} تومان` : '---'}
                      </td>
                      <td className="px-6 py-4">{getStatusBadge(req.status)}</td>
                      <td className="px-6 py-4 text-gray-500 text-sm">
                        {new Date(req.created_at).toLocaleDateString('fa-IR')}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                          <Link
                            to={`/admin/installations/${req.id}`}
                            className="text-blue-600 hover:text-blue-800"
                            title="مشاهده جزئیات"
                          >
                            <Eye className="w-5 h-5" />
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* صفحه‌بندی */}
        {totalPages > 1 && (
          <div className="flex justify-center gap-2 mt-6">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-3 py-1 border rounded-lg disabled:opacity-50"
            >
              قبلی
            </button>
            <span className="px-4 py-1">صفحه {currentPage} از {totalPages}</span>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-3 py-1 border rounded-lg disabled:opacity-50"
            >
              بعدی
            </button>
          </div>
        )}
      </div>
    </>
  );
};